import { Injectable, inject } from "@angular/core";
import { Observable, from, of, timer } from "rxjs";
import {
  catchError,
  exhaustMap,
  filter,
  map,
  switchMap,
  take,
  timeout,
} from "rxjs/operators";
import { ExperimentService } from "src/app/shared/services/experiment.service";
import { EXPERIMENT_KEYS } from "src/app/experiments/experiment-keys.index";
import { DateService } from "src/app/shared/services/date.service";
import { MixpanelService } from "src/app/shared/services/mixpanel.service";
import { StorageService } from "src/app/shared/services/storage.service";

/**
 * 🚩 Auto‑generated helper service for the "POOM-FE-2470-Redirect-authenticated-users-away-from-landing-page" experiment key
 *
 * All public methods are strongly‑typed and tree‑shakeable.
 * You may freely extend or delete this file when the experiment key is removed.
 *
 * Experiment type: boolean
 * Default value: false
 */
@Injectable({ providedIn: "root" })
export class RedirectAuthenticatedUsersAwayFromLandingPageExperimentKeyService {
  private static readonly READINESS_TIMEOUT_MS = 5000;
  private static readonly AUTHENTICATED_ATTRIBUTES_POLL_INTERVAL_MS = 100;
  private redirectedToMainAfterEnabledFlag = false;

  /** DI without constructor boilerplate (Angular 14+) */
  private readonly experimentService = inject(ExperimentService);
  private readonly storageService = inject(StorageService);
  private readonly mixpanelService = inject(MixpanelService);
  private readonly dateService = inject(DateService);

  private static readonly KEY =
    "POOM-FE-2470-Redirect-authenticated-users-away-from-landing-page" as const;

  /** Reactive stream of the experiment key value after GrowthBook is ready */
  value$(): Observable<boolean> {
    return this.valueWhenReady$();
  }

  /** Read the experiment key only after GrowthBook initialization completes. */
  valueWhenReady$(): Observable<boolean> {
    return this.experimentService.valueWhenReady$(
      RedirectAuthenticatedUsersAwayFromLandingPageExperimentKeyService.KEY,
      EXPERIMENT_KEYS[
        "POOM-FE-2470-Redirect-authenticated-users-away-from-landing-page"
      ],
      { refreshOnSubscribe: true }
    );
  }

  /** Synchronous snapshot (guards, interceptors, etc.) */
  get() {
    return this.experimentService.get(
      RedirectAuthenticatedUsersAwayFromLandingPageExperimentKeyService.KEY,
      EXPERIMENT_KEYS[
        "POOM-FE-2470-Redirect-authenticated-users-away-from-landing-page"
      ]
    );
  }

  /** Raw synchronous value from GrowthBook for the redirect flag. */
  getRaw() {
    const result = this.experimentService.feature(
      RedirectAuthenticatedUsersAwayFromLandingPageExperimentKeyService.KEY
    );
    const value = result?.value ?? null;

    if (value !== false) {
      return value;
    }

    if (this.isFallbackEvaluation(result)) {
      return this.hasAuthenticatedTargetingAttributes() ? false : null;
    }

    return false;
  }

  /** Convenience boolean */
  isOn(defaultVal = false) {
    return this.experimentService.isOn(
      RedirectAuthenticatedUsersAwayFromLandingPageExperimentKeyService.KEY,
      defaultVal
    );
  }

  /**
   * Emits a boolean redirect decision after GrowthBook is ready, with a bounded
   * fallback so landing activation cannot hang when initialization fails.
   */
  isOnWhenReady$(
    timeoutMs = RedirectAuthenticatedUsersAwayFromLandingPageExperimentKeyService.READINESS_TIMEOUT_MS
  ): Observable<boolean> {
    return this.valueWhenReady$().pipe(
      take(1),
      timeout({
        first: timeoutMs,
        with: () => of(false),
      }),
      map((value) => value === true),
      catchError(() => of(false))
    );
  }

  hasAuthenticatedAttributesForUser(userId: string): boolean {
    try {
      const attributes = this.experimentService.getAttributes();
      return attributes.id === userId && attributes.isAnonymous === false;
    } catch {
      return false;
    }
  }

  private hasAuthenticatedTargetingAttributes(): boolean {
    try {
      return this.experimentService.getAttributes().isAnonymous === false;
    } catch {
      return false;
    }
  }

  private isFallbackEvaluation(result: { source?: string } | null): boolean {
    return (
      result?.source === "defaultValue" || result?.source === "unknownFeature"
    );
  }

  isOnWhenReadyForAuthenticatedUser$(
    userId: string,
    timeoutMs = RedirectAuthenticatedUsersAwayFromLandingPageExperimentKeyService.READINESS_TIMEOUT_MS
  ): Observable<boolean> {
    return timer(
      0,
      RedirectAuthenticatedUsersAwayFromLandingPageExperimentKeyService.AUTHENTICATED_ATTRIBUTES_POLL_INTERVAL_MS
    ).pipe(
      exhaustMap(() => {
        return this.ensureAuthenticatedAttributesForUser$(userId).pipe(
          switchMap((hasAuthenticatedAttributes) => {
            if (!hasAuthenticatedAttributes) {
              return of(null);
            }

            return this.isOnWhenReady$(timeoutMs).pipe(
              take(1),
              map((isRedirectEnabled) =>
                this.hasAuthenticatedAttributesForUser(userId)
                  ? isRedirectEnabled
                  : null
              )
            );
          })
        );
      }),
      filter(
        (isRedirectEnabled): isRedirectEnabled is boolean =>
          isRedirectEnabled !== null
      ),
      take(1),
      timeout({
        first: timeoutMs,
        with: () => of(false),
      }),
      catchError(() => of(false))
    );
  }

  private ensureAuthenticatedAttributesForUser$(
    userId: string
  ): Observable<boolean> {
    if (this.hasAuthenticatedAttributesForUser(userId)) {
      return of(true);
    }

    return from(this.applyAuthenticatedAttributesForUser(userId)).pipe(
      map(() => this.hasAuthenticatedAttributesForUser(userId)),
      catchError(() => of(false))
    );
  }

  private async applyAuthenticatedAttributesForUser(
    userId: string
  ): Promise<void> {
    const [userData, bankData, { id: anonymousId, source }] = await Promise.all(
      [
        this.storageService.get("user"),
        this.storageService.get("userBank"),
        this.mixpanelService.getDeviceId(),
      ]
    );

    await this.experimentService.initAuthenticated({
      id: userId,
      user_id: userId,
      anonymous_id: anonymousId,
      id_source: source,
      country: userData?.country,
      bankId: bankData?.id ?? null,
      registrationDate: this.dateService.safeFormatToISO(
        userData?.registrationDate
      ),
      isB2B2CUser: Boolean(userData?.B2B2CEmployerId),
      isAnonymous: false,
    });
  }

  markRedirectedToMainAfterEnabledFlag(): void {
    this.redirectedToMainAfterEnabledFlag = true;
  }

  consumeRedirectedToMainAfterEnabledFlag(): boolean {
    const redirectedToMainAfterEnabledFlag =
      this.redirectedToMainAfterEnabledFlag;
    this.redirectedToMainAfterEnabledFlag = false;
    return redirectedToMainAfterEnabledFlag;
  }

  /**
   * Run side‑effects only when experiment key becomes true, then auto‑unsubscribe
   *
   * This method automatically unsubscribes after the first time the experiment
   * becomes true, preventing memory leaks. If you need continuous monitoring,
   * use value$() directly and manage the subscription lifecycle yourself.
   *
   * @param fn - Function to execute when experiment becomes true
   * @returns Subscription that will auto‑complete after first execution
   */
  whenOn(fn: () => void) {
    return this.value$()
      .pipe(
        filter((v) => v),
        take(1)
      )
      .subscribe((v) => v && fn());
  }
}
